import Swiper from "./swiper-bundle.esm.browser.min.js";

/**
 * <product-banner-section>
 *
 * Drives the mobile-first product banner:
 *  - syncs the main photo swiper with the plain thumbnail row
 *  - mirrors the REAL, visible Kaching Bundles widget (reskinned via CSS to
 *    look like this section's cards) into the read-only "Subscribe & Save" /
 *    "One-Time" summary panels below it
 *
 * Important: this script never clicks, submits, or otherwise drives Kaching
 * Bundles into a DIFFERENT state than what's already visibly selected. An
 * earlier version forwarded synthetic clicks into a HIDDEN COPY of the
 * widget so a fully custom UI could drive it — that broke real cart
 * requests (cart/change 422s), because it made Kaching juggle two divergent
 * states. The real widget is now shown directly (CSS-reskinned to match the
 * design), sitting inside a real Shopify product-form (see
 * sections/product-banner.liquid) exactly like this theme's own bundle apps
 * do on main-product.liquid — so Kaching writes the selected
 * variant/selling_plan into that form itself, and the shared <product-form>
 * component (assets/product-form.js) handles the actual submit generically.
 *
 * Confirmed live: Kaching's own selling_plan form field never actually
 * resolves ahead of the real submit on this integration (stays blank the
 * entire time the bar picker is up, however long you wait) — so the CTA
 * button's price is computed from the bar that's visibly selected (its
 * --selected class, known for certain) plus quantity read from the form
 * (which Kaching DOES write reliably), not from selling_plan at all. This
 * file only ever READS Kaching's DOM to keep the summary panels + that
 * price in sync — nothing here can interfere with checkout.
 *
 * "Subscribe & Save" vs "One-Time Order" no longer touches Kaching's bar
 * selection at all (an earlier version deselected every bar and clicked
 * its "Buy Once" control when One-Time was chosen — removed). A bundle bar
 * picked while One-Time is active now just means "this many units," not
 * "this selling plan" — Kaching is left completely alone so its own
 * quantity field keeps working normally in both modes.
 *
 * Getting the ACTUAL Add to Cart request to drop the subscription turned
 * out not to be a matter of clearing one form field: Kaching's own
 * selling_plan write happens "at the actual moment of submit" (see above)
 * specifically BECAUSE a bar is still marked --selected, and that class is
 * only a reflection of Kaching's own internal selection state, not the
 * source of it — toggling the class ourselves (confirmed dead-end, see git
 * history) doesn't change what Kaching itself thinks is selected, so
 * deleting the field from a `formdata` listener kept losing the race to
 * Kaching re-asserting it right at submit. bindOneTimeCheckout() instead
 * fully preempts the click in the capture phase at `document` — which
 * always runs before ANY listener on/under the button, regardless of
 * script load order — and submits a hand-built request (variant id +
 * quantity only, both read from the form Kaching already populated
 * correctly) directly to /cart/add.js, so Kaching's own submit-time hook
 * never runs at all for a one-time purchase. Subscribe mode is completely
 * untouched by this — the click proceeds normally, exactly as before.
 *
 * The Kaching Bundles selector names below (`kaching-bundles__...`) match the
 * markup rendered by that app as of 2026-08-04. If the app updates its DOM
 * this sync layer may need to be re-verified against the live widget.
 */

if (!customElements.get("product-banner-section")) {
  customElements.define(
    "product-banner-section",
    class ProductBannerSection extends HTMLElement {
      connectedCallback() {
        this.syncGallery();
        this.watchNativeWidget();
        this.bindPurchaseCards();
        this.watchYotpoRating();

        window.debugProductBanner = () => this.debugKaching();
      }

      disconnectedCallback() {
        clearInterval(this._galleryPoll);
        clearInterval(this._widgetPoll);
        clearInterval(this._yotpoRatingPoll);
        this._widgetObserver?.disconnect();
        this._thumbsSwiper?.destroy(true, true);
        // Registered on `document`, not on this element — outlives a
        // detach/reattach (e.g. the theme editor re-rendering this
        // section) unless removed explicitly here.
        if (this._oneTimeClickHandler) {
          document.removeEventListener("click", this._oneTimeClickHandler, { capture: true });
        }
      }

      /* ---------------------------------------------------------------- */
      /* Gallery: plain thumbnail row driving the main swiper               */
      /* ---------------------------------------------------------------- */

      syncGallery() {
        this._galleryPoll = setInterval(() => {
          const main = this.querySelector(".wt-product-banner__main-swiper")
            ?.swiper;

          if (main) {
            clearInterval(this._galleryPoll);
            this.initGallerySync(main);
          }
        }, 100);
      }

      initGallerySync(main) {
        const thumbEls = Array.from(
          this.querySelectorAll(".wt-product-banner__thumb"),
        );

        const setActiveThumb = (index) => {
          thumbEls.forEach((el, i) =>
            el.classList.toggle("is-active", i === index),
          );
        };

        main.on("slideChange", () => setActiveThumb(main.activeIndex));

        thumbEls.forEach((el, i) => {
          el.addEventListener("click", () => {
            main.slideTo(i);
            setActiveThumb(i);
          });
        });

        this.setupDesktopThumbsSwiper(main);
      }

      // Desktop only: upgrades the plain thumbnail row into a real vertical
      // Swiper instance wired to the main gallery swiper via Swiper's Thumbs
      // module — same mechanism and class names (swiper-thumbs, swiper-vertical,
      // wt-slider__container--thumbs, swiper-thumbs-ready, swiper-at-beginning,
      // ...) as the native PDP gallery (gallery.js / module-product-gallery.css).
      // Mobile keeps the plain horizontal-scroll row, so this only ever runs
      // above the 1000px breakpoint, and tears itself down if the viewport
      // crosses back below it (e.g. resizing the window, not just phones).
      setupDesktopThumbsSwiper(main) {
        const mq = window.matchMedia("(min-width: 1000px)");
        const row = this.querySelector(".wt-product-banner__thumbs-row");
        const list = this.querySelector(".wt-product-banner__thumbs-list");
        if (!row || !list) return;

        const enable = () => {
          if (this._thumbsSwiper) return;

          list.classList.add("swiper-wrapper");
          Array.from(list.children).forEach((el) =>
            el.classList.add("swiper-slide"),
          );
          row.classList.add(
            "swiper-thumbs",
            "swiper-vertical",
            "wt-slider__container",
            "wt-slider__container--thumbs",
          );

          // slidesPerView must stay "auto" — a fixed number (e.g. 6, like the
          // native PDP gallery) makes Swiper divide the column's own height
          // by that count and set its OWN computed height per slide (that's
          // where the unwanted 80px came from), ignoring our own 49x49 thumb
          // size entirely. With "auto", each slide's size is read from CSS —
          // the actual fixed size lives on .wt-product-banner__thumb itself
          // (!important, since swiper.css's own `.swiper-slide { height:100% }`
          // default would otherwise stretch every thumb to the full column
          // height — that one giant stretched thumb was today's bug).
          this._thumbsSwiper = new Swiper(row, {
            direction: "vertical",
            slidesPerView: "auto",
            spaceBetween: 8,
            freeMode: true,
            watchSlidesProgress: true,
          });

          main.thumbs.swiper = this._thumbsSwiper;
          main.thumbs.init();
          main.thumbs.update(true);

          const updateEdgeClasses = () => {
            row.classList.add("swiper-thumbs-ready");
            row.classList.toggle(
              "swiper-at-beginning",
              this._thumbsSwiper.isBeginning,
            );
            row.classList.toggle("swiper-at-end", this._thumbsSwiper.isEnd);
          };
          this._thumbsSwiper.on("slideChange", updateEdgeClasses);
          this._thumbsSwiper.on("resize", updateEdgeClasses);
          updateEdgeClasses();
        };

        const disable = () => {
          if (!this._thumbsSwiper) return;

          this._thumbsSwiper.destroy(true, true);
          this._thumbsSwiper = null;
          if (main.thumbs) main.thumbs.swiper = null;

          row.classList.remove(
            "swiper",
            "swiper-thumbs",
            "swiper-vertical",
            "wt-slider__container",
            "wt-slider__container--thumbs",
            "swiper-initialized",
            "swiper-pointer-events",
            "swiper-thumbs-ready",
            "swiper-at-beginning",
            "swiper-at-end",
          );
          list.classList.remove("swiper-wrapper");
          Array.from(list.children).forEach((el) =>
            el.classList.remove("swiper-slide", "swiper-slide-thumb-active"),
          );
        };

        const sync = () => (mq.matches ? enable() : disable());
        sync();
        mq.addEventListener("change", sync);
      }

      /* ---------------------------------------------------------------- */
      /* Yotpo star-rating app block — inject the numeric average           */
      /* ---------------------------------------------------------------- */

      // Mounted async by Yotpo's own script (hence the poll), and this
      // particular widget layout only ever renders stars + a review COUNT
      // ("134 Reviews") — no numeric average like "4.5 | 134 Reviews". The
      // button's own aria-label already carries that number ("4.5 out 5
      // stars rating in total 134 reviews..."), so it's read from there
      // instead of needing a second Yotpo API call.
      watchYotpoRating() {
        this._yotpoRatingPoll = setInterval(() => {
          const btn = this.querySelector(
            ".wt-product-banner__reviews .yotpo-sr-bottom-line-summary",
          );
          const textEl = btn?.querySelector(".yotpo-sr-bottom-line-text");
          if (btn && textEl) {
            clearInterval(this._yotpoRatingPoll);
            this.injectYotpoRatingNumber(btn, textEl);
          }
        }, 200);

        // Stop polling after 10s if the app block never mounts.
        setTimeout(() => clearInterval(this._yotpoRatingPoll), 10000);
      }

      injectYotpoRatingNumber(btn, textEl) {
        const match = btn.getAttribute("aria-label")?.match(/([\d.]+)\s*out/);
        if (!match) return;
        textEl.textContent = `${match[1]} | ${textEl.textContent.trim()}`;
      }

      /* ---------------------------------------------------------------- */
      /* Kaching Bundles — read-only mirroring                              */
      /* ---------------------------------------------------------------- */

      nativeRoot() {
        return this.querySelector("[data-app-blocks]");
      }

      nativeBars() {
        return Array.from(
          this.nativeRoot()?.querySelectorAll(".kaching-bundles__bar") || [],
        );
      }

      // Clicking either card ALWAYS activates it visually right away, and
      // never touches Kaching at all — no class stripping, no synthetic
      // clicks into its widget. Which bundle bar is highlighted is now
      // entirely Kaching's own business regardless of which card is active;
      // this only records which card the shopper picked (this._purchaseMode)
      // so renderFromWidget() knows which price to show and
      // bindOneTimeCheckout() knows whether to intercept the actual submit.
      bindPurchaseCards() {
        const subscribeCard = this.querySelector('[data-purchase-card="subscribe"]');
        const onetimeCard = this.querySelector('[data-purchase-card="onetime"]');
        this._purchaseMode = "subscribe";
        this._modeJustChanged = false;

        onetimeCard?.addEventListener("click", () => {
          this._purchaseMode = "onetime";
          // See renderFromWidget()/updateCtaPriceForBar() — the very next
          // render after a mode switch has to force the CTA price write,
          // otherwise the "don't fight Kaching's own write" guard in
          // updateCtaPrice() sees the price left over from the *other*
          // mode, assumes Kaching already wrote it, and leaves the stale
          // number showing.
          this._modeJustChanged = true;
          subscribeCard?.classList.remove("is-active");
          onetimeCard.classList.add("is-active");
          this.renderFromWidget();
        });

        subscribeCard?.addEventListener("click", () => {
          this._purchaseMode = "subscribe";
          this._modeJustChanged = true;
          onetimeCard?.classList.remove("is-active");
          subscribeCard.classList.add("is-active");
          this.renderFromWidget();
        });

        this.bindOneTimeCheckout();
      }

      // Kaching adds its subscription bundle from an EARLY pointer event on the
      // CTA — it reads the selected bar on pointerdown/mousedown, BEFORE `click`,
      // and re-asserts the bar selection, so a click-phase intercept (or
      // deselecting the bar) is always too late (verified live: the sub still got
      // added). So we swallow pointerdown + mousedown + click on the CTA at the
      // very top of the capture phase (`window`, which runs before document/form/
      // button listeners), preempting Kaching entirely, and run our OWN pure-fetch
      // one-time add on the click — a pure /cart/add of {id, quantity} that
      // Kaching never converts after the fact (also verified live). Subscribe mode
      // is left completely untouched.
      bindOneTimeCheckout() {
        const oneTimeCtaFromEvent = (event) => {
          if (this._purchaseMode !== "onetime") return null;
          const button = this.querySelector(".wt-product-banner__cta");
          if (!button || !button.contains(event.target)) return null;
          return button;
        };

        // Kill the early pointer/mouse events so Kaching never gets to act on the
        // click at all.
        this._oneTimeSwallow = (event) => {
          if (!oneTimeCtaFromEvent(event)) return;
          event.preventDefault();
          event.stopImmediatePropagation();
        };

        // The click itself is where WE do the one-time add.
        this._oneTimeClickHandler = (event) => {
          const button = oneTimeCtaFromEvent(event);
          if (!button) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.submitOneTimeOrder(button);
        };

        window.addEventListener("pointerdown", this._oneTimeSwallow, { capture: true });
        window.addEventListener("mousedown", this._oneTimeSwallow, { capture: true });
        window.addEventListener("click", this._oneTimeClickHandler, { capture: true });
      }

      // Builds the Add to Cart request ourselves — variant id + quantity
      // only, read straight off the fields Kaching already populated
      // correctly, but never handed to Kaching (or <product-form>) to
      // submit. That's deliberate: it's the only way to guarantee no
      // selling_plan rides along, short of understanding exactly how/when
      // Kaching's own submit-time write happens.
      async submitOneTimeOrder(button) {
        const idField = this.querySelector('form [name="id"]');
        const qtyField = this.querySelector('form [name="quantity"]');
        const variantId = idField ? parseInt(idField.value, 10) : null;
        const qty = parseInt(qtyField?.value, 10) || 1;
        if (!variantId) return;

        button.setAttribute("aria-disabled", "true");
        button.classList.add("loading");

        // Same drawer this theme's own <product-form> (assets/product-form.js)
        // uses — requesting `sections`/`sections_url` alongside the add is
        // what makes cart/add.js return rendered HTML for the drawer instead
        // of just the line-item JSON, and cart.renderContents() is what
        // actually opens it (isClosedCart defaults to true there).
        const cart = document.querySelector("cart-drawer");
        cart?.setActiveElement(document.activeElement);

        const payload = { id: variantId, quantity: qty };
        if (cart && typeof cart.getSectionsToRender === "function") {
          payload.sections = cart.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
        }

        try {
          const response = await fetch(window.routes.cart_add_url, {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify(payload),
          });
          const data = await response.json();
          if (data.status) {
            console.error("[one-time checkout] add to cart failed:", data);
            return;
          }

          if (window.publish && window.PUB_SUB_EVENTS) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
              source: "product-banner-onetime",
              productVariantId: variantId,
              cartData: data,
            });
          }

          if (cart && typeof cart.renderContents === "function") {
            cart.renderContents(data);
          } else {
            // No drawer on this page (or it's disabled) — the cart page
            // itself always reflects the real cart state, so it's the
            // one fallback that can't show a stale/wrong total.
            window.location = window.routes.cart_url;
          }
        } catch (e) {
          console.error("[one-time checkout] add to cart error:", e);
        } finally {
          button.removeAttribute("aria-disabled");
          button.classList.remove("loading");
        }
      }

      // The price lives as a plain text node directly inside
      // .kaching-bundles__bar-price / .kaching-bundles__bar-full-price,
      // alongside a nested <span class="...unit-label"> ("/mo") — reading
      // .textContent on the whole div would glue both together, so this
      // grabs only the direct text node (the actual "$42" part).
      directText(el) {
        if (!el) return "";
        const textNode = Array.from(el.childNodes).find(
          (n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim(),
        );
        return textNode ? textNode.textContent.trim() : "";
      }

      // Kaching's `data-a11y-label` attribute is a static, untranslated
      // accessibility string (always e.g. "system.price") — it is NOT where
      // the resolved price lives, so never trust a value that looks like it.
      looksResolved(value) {
        return !!value && /\d/.test(value);
      }

      readBarData(bar) {
        const priceEl = bar.querySelector(".kaching-bundles__bar-price");
        const fullPriceEl = bar.querySelector(".kaching-bundles__bar-full-price");
        return {
          label: bar.querySelector(".kaching-bundles__bar-label")?.textContent.trim(),
          price: this.directText(priceEl),
          fullPrice: this.directText(fullPriceEl),
          selected: bar.classList.contains("kaching-bundles__bar--selected"),
        };
      }

      watchNativeWidget() {
        this._widgetPoll = setInterval(() => {
          const bars = this.nativeBars();
          if (bars.length) {
            clearInterval(this._widgetPoll);
            this.initWidgetSync();
          }
        }, 200);

        // Stop polling after 10s if the bundle app never renders (plain product).
        setTimeout(() => clearInterval(this._widgetPoll), 10000);
      }

      initWidgetSync() {
        const root = this.nativeRoot();
        if (!root || this._widgetObserver) return;

        // Debounced, not just batched to the next frame: Kaching updates its
        // DOM in more than one step after a click (its own intermediate
        // mutations can land before it actually marks a bar "--selected").
        // Reacting on the very first mutation could catch that in-between
        // state — no bar selected yet — and our optimistic "Subscribe & Save"
        // highlight would get flipped back off, which read as "first click
        // does nothing, second click works" (the second click landed once
        // the first one's async update had already settled). Waiting a short
        // idle period after the LAST mutation lets it finish before we trust
        // the DOM.
        let debounceTimer = null;
        const scheduleRender = () => {
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => this.renderFromWidget(), 200);
        };

        // Observes the whole <form>, not just the Kaching wrap — the
        // id/selling_plan hidden inputs Kaching writes into sit as siblings
        // of [data-app-blocks] (children of the surrounding form), not
        // descendants of it, so watching only the Kaching wrap could miss
        // exactly the mutation this needs to react to.
        //
        // That same form also contains the CTA button and the One-Time
        // card's own price element, both of which WE write to (see
        // updateCtaPrice()/updateOneTimeCardPrice()) — deliberately NOT
        // filtered out here (an earlier version did, to avoid an
        // infinite render loop), because Kaching turned out to write
        // directly into the CTA price too, on its own delayed timing,
        // overwriting our one-time total with its subscription price a
        // moment after we'd correctly set it. Reacting to those mutations
        // is what lets renderFromWidget() notice and correct that. The
        // signature check in renderFromWidget() is what actually prevents
        // the infinite loop now (it includes the rendered price text
        // itself, so once we've (re)written the correct value, seeing that
        // same value again is a no-op — only an actual change, ours or
        // Kaching's, triggers real work).
        //
        // enforceBundlePrices() runs on every mutation batch, NOT debounced
        // like the rest of the render — confirmed live, waiting for the
        // 200ms debounce meant Kaching's wrong price stayed visible for a
        // noticeable flash before getting corrected. Fixing just the
        // prices on the spot (it no-ops once already correct, so it can't
        // loop) shrinks that window to well under a frame.
        this._widgetObserver = new MutationObserver(() => {
          this.enforceBundlePrices();
          scheduleRender();
        });
        this._widgetObserver.observe(this.querySelector("form") || root, {
          attributes: true,
          subtree: true,
          characterData: true,
          childList: true,
        });

        // One check right after the widget mounts (its bars may not have
        // settled into a selected state yet at this exact instant), then
        // the observer above takes over — one debounced render per actual
        // change from here on, not a fixed polling schedule.
        this.renderFromWidget();
      }

      renderFromWidget() {
        const bars = this.nativeBars();
        const selectedIndex = bars.findIndex((bar) =>
          bar.classList.contains("kaching-bundles__bar--selected"),
        );
        const selected = selectedIndex >= 0 ? this.readBarData(bars[selectedIndex]) : null;

        // Writing our own price/text updates below is itself a DOM
        // mutation — the same observer that calls renderFromWidget() also
        // watches those writes, so without a guard every render schedules
        // another one forever. Including the RENDERED price text itself
        // (not just the inputs that feed it) is what makes this
        // self-correcting rather than just "run once": if Kaching later
        // overwrites our one-time price with its own subscription price
        // (confirmed live — happens on a delay after switching bundles),
        // the next mutation's signature won't match what we last rendered,
        // so it re-renders and fixes it. Re-rendering the exact same
        // result twice in a row IS a no-op though, which is what actually
        // breaks the infinite loop.
        const signatureOf = () =>
          [
            selectedIndex,
            this._purchaseMode,
            this.querySelector('form [name="id"]')?.value,
            this.querySelector('form [name="quantity"]')?.value,
            this.querySelector("[data-cta-sale]")?.textContent,
            this.querySelector("[data-price-value]")?.textContent,
            this.querySelector("[data-onetime-price]")?.textContent,
          ].join("|");

        if (signatureOf() === this._lastRenderSignature) return;

        // Both cards' own prices follow whichever bundle is highlighted
        // regardless of which purchase mode is active — only the CTA
        // button (and which card looks "active") depends on that, since
        // that's the only part that reflects what actually gets charged.
        if (selectedIndex >= 0) {
          this.renderBulletsFor(selectedIndex);
          this.renderBadgeFor(selectedIndex);
          this.renderShipsFor(selectedIndex);
          this.updateSavingsBullet(selectedIndex);

          const priceValueEl = this.querySelector("[data-price-value]");
          const saveEl = this.querySelector("[data-purchase-save]");
          if (priceValueEl && this.looksResolved(selected.price)) priceValueEl.textContent = selected.price;
          if (saveEl && this.looksResolved(selected.label)) saveEl.textContent = selected.label;
        }
        this.updateOneTimeCardPrice(selectedIndex);

        const subscribeCard = this.querySelector('[data-purchase-card="subscribe"]');
        const onetimeCard = this.querySelector('[data-purchase-card="onetime"]');
        subscribeCard?.classList.toggle("is-active", this._purchaseMode === "subscribe");
        onetimeCard?.classList.toggle("is-active", this._purchaseMode === "onetime");

        if (this._purchaseMode === "onetime") {
          this.updateOneTimeCtaPrice(selectedIndex);
        } else if (!selected) {
          const onetimePriceEl = this.querySelector("[data-onetime-price]");
          this.updateCtaPrice({
            sale: onetimePriceEl?.textContent.trim(),
            compare: null,
            force: this._modeJustChanged,
          });
        } else {
          // The CTA button's price uses the bar that's actually selected
          // (known for certain from its --selected class) to look up the
          // right plan, combined with quantity read live from the form.
          // Confirmed live: Kaching's own selling_plan form field never
          // resolves ahead of the real submit — it stays blank the entire
          // time the picker is up — so reading it early for a price
          // preview doesn't work; going by bar index sidesteps needing it
          // at all.
          this.updateCtaPriceForBar(selectedIndex, this._modeJustChanged);
        }

        // One-shot — only the render immediately following a purchase-card
        // click should force past the "Kaching already wrote it" guard.
        // Left set, every later render (e.g. Kaching's own bundle-bar
        // switches while staying in Subscribe mode) would also force,
        // which is exactly the fighting-with-Kaching's-synchronous-write
        // behavior that guard exists to prevent.
        this._modeJustChanged = false;

        // Captured fresh AFTER our own writes above, not before — this is
        // what lets the next render tell "nothing changed" (this exact
        // signature) apart from "Kaching changed something" (a different
        // one), rather than comparing against stale pre-write state.
        this._lastRenderSignature = signatureOf();
      }

      // The "One-Time Order" row's own price — qty (from whichever bundle
      // is highlighted) × plain unit price, no selling_plan/allocation
      // involved. Runs regardless of which purchase card is active (same
      // as the Subscribe & Save card's own price, updated alongside it in
      // renderFromWidget()) — only the CTA button cares which mode is
      // actually selected, since that's what determines what's charged.
      updateOneTimeCardPrice(index) {
        const onetimePriceEl = this.querySelector("[data-onetime-price]");
        const pricing = index >= 0 ? this.resolveBarPricing(index) : null;
        if (!onetimePriceEl || !pricing) return;

        const { variant, qty } = pricing;
        onetimePriceEl.textContent = this.stripZeroCents(this.formatTotal(variant.pf, variant.p, variant.p * qty));
      }

      // The CTA button's price when One-Time is the active card — same
      // qty × unit price as updateOneTimeCardPrice(), just written to the
      // button instead of the card, and via updateCtaPrice() (which knows
      // how to fully hide the button's price row if `sale` comes back
      // empty).
      updateOneTimeCtaPrice(index) {
        const onetimePriceEl = this.querySelector("[data-onetime-price]");
        const pricing = index >= 0 ? this.resolveBarPricing(index) : null;

        if (!pricing) {
          // No bundle highlighted (or pricing data isn't ready yet) — the
          // card's current price, whatever that resolved to.
          this.updateCtaPrice({ sale: onetimePriceEl?.textContent.trim(), compare: null });
          return;
        }

        const { variant, qty } = pricing;
        const totalFormatted = this.stripZeroCents(this.formatTotal(variant.pf, variant.p, variant.p * qty));

        // force: true — updateCtaPrice()'s "don't overwrite" guard exists
        // to avoid fighting Kaching's own synchronous price write during
        // the subscribe flow, but Kaching never writes anything here in
        // one-time mode; without forcing it, a price left over from an
        // earlier subscribe render (or an earlier bundle size) never gets
        // replaced, which is exactly why the button kept showing the old
        // subscription total instead of this qty's full price.
        this.updateCtaPrice({ sale: totalFormatted, compare: null, force: true });
      }

      // Same computation as updateOneTimeCardPrice()/updateOneTimeCtaPrice(),
      // called on every mutation (see initWidgetSync()) instead of only on
      // the debounced render — checks first and only writes if the text
      // has actually drifted from the expected value. That check is what
      // keeps this from looping: writing the same string again would
      // itself be a mutation (textContent replaces its text node even
      // when the value is unchanged), so skipping the write once already
      // correct is what lets it settle instead of re-triggering itself
      // forever.
      enforceBundlePrices() {
        const bars = this.nativeBars();
        const selectedIndex = bars.findIndex((bar) =>
          bar.classList.contains("kaching-bundles__bar--selected"),
        );

        if (selectedIndex >= 0) {
          const selected = this.readBarData(bars[selectedIndex]);
          const priceValueEl = this.querySelector("[data-price-value]");
          const saveEl = this.querySelector("[data-purchase-save]");
          if (priceValueEl && this.looksResolved(selected.price) && priceValueEl.textContent !== selected.price) {
            priceValueEl.textContent = selected.price;
          }
          if (saveEl && this.looksResolved(selected.label) && saveEl.textContent !== selected.label) {
            saveEl.textContent = selected.label;
          }
        }

        const pricing = selectedIndex >= 0 ? this.resolveBarPricing(selectedIndex) : null;
        if (!pricing) return;
        const { variant, qty } = pricing;
        const totalFormatted = this.stripZeroCents(this.formatTotal(variant.pf, variant.p, variant.p * qty));

        const onetimePriceEl = this.querySelector("[data-onetime-price]");
        if (onetimePriceEl && onetimePriceEl.textContent !== totalFormatted) {
          onetimePriceEl.textContent = totalFormatted;
        }

        if (this._purchaseMode !== "onetime") return;
        const saleFormatted = this.fmtPrice(totalFormatted);
        const saleEl = this.querySelector("[data-cta-sale]");
        if (saleEl && saleEl.textContent !== saleFormatted) {
          saleEl.textContent = saleFormatted;
        }
        const compareEl = this.querySelector("[data-cta-compare]");
        if (compareEl && compareEl.style.display !== "none") {
          compareEl.style.display = "none";
        }
      }

      // Lazily parsed + cached — see data-sp-data in the liquid (same
      // {id, pf, cf, a:[{id, pf, cf}]} shape as this theme's own native
      // wt-sp-data-{{ section.id }} in main-product.liquid).
      spData() {
        if (this._spData) return this._spData;
        const el = this.querySelector("[data-sp-data]");
        try {
          this._spData = el ? JSON.parse(el.textContent) : [];
        } catch (e) {
          this._spData = [];
        }
        return this._spData;
      }

      // Lazily parsed + cached — see data-bar-plan-ids in the liquid
      // (index 0/1/2 = monthly/3-month/6-month, same order as the bars).
      barPlanIds() {
        if (this._barPlanIds) return this._barPlanIds;
        const el = this.querySelector("[data-bar-plan-ids]");
        try {
          this._barPlanIds = el ? JSON.parse(el.textContent) : [];
        } catch (e) {
          this._barPlanIds = [];
        }
        return this._barPlanIds;
      }

      // Same string-replace trick as this theme's own native update()
      // (sections/main-product.liquid): swaps the new total into the
      // already-formatted price string in place of the per-unit number, so
      // whatever currency symbol/format the shop uses is preserved exactly
      // without needing a separate money-formatting helper here.
      formatTotal(formatted, perUnitCents, totalCents) {
        const perUnitNum = (perUnitCents / 100).toFixed(2);
        const totalNum = (totalCents / 100).toFixed(2);
        return formatted.replace(perUnitNum, totalNum);
      }

      // Drops whole-number cents ("$375.00" -> "$375") while leaving real
      // cents intact ("$375.50" stays). Used only for the one-time price.
      stripZeroCents(str) {
        return typeof str === "string" ? str.replace(/[.,]00(?=\D|$)/, "") : str;
      }

      // Looks up the plan that bar index actually means (data-bar-plan-ids,
      // reliable — the merchant's own group order) and combines it with
      // quantity read live from the form (Kaching writes this reliably,
      // unlike selling_plan). Shared by updateCtaPriceForBar() and
      // updateSavingsBullet() — both need the same variant/allocation/qty
      // lookup, just to compute different numbers from it. Returns null if
      // anything isn't ready yet; callers just skip their update for now.
      resolveBarPricing(index) {
        const barPlanIds = this.barPlanIds();
        const planId = barPlanIds[index];
        if (!planId) return null;

        const idField = this.querySelector('form [name="id"]');
        const qtyField = this.querySelector('form [name="quantity"]');
        const variantId = idField ? parseInt(idField.value, 10) : null;
        const qty = qtyField ? parseInt(qtyField.value, 10) : null;
        if (!variantId || !qty) return null;

        const variant = this.spData().find((v) => v.id === variantId);
        if (!variant) return null;

        const allocation = variant.a.find((a) => a.id === planId);
        if (!allocation) return null;

        return { variant, allocation, qty };
      }

      // Computed from PER-UNIT prices, not the qty-scaled totals — the
      // ratio (and so the percentage) is the same either way, so there's
      // no need to redo this once qty is multiplied in below.
      discountPercent(saleUnitCents, compareUnitCents) {
        if (!compareUnitCents || compareUnitCents <= saleUnitCents) return null;
        return Math.round((1 - saleUnitCents / compareUnitCents) * 100);
      }

      updateCtaPriceForBar(index, force = false) {
        const pricing = this.resolveBarPricing(index);
        if (!pricing) return;
        const { variant, allocation, qty } = pricing;
        const compareUnit = allocation.c || variant.p;
        const discount = this.discountPercent(allocation.p, compareUnit);

        if (qty <= 1) {
          this.updateCtaPrice({
            sale: allocation.pf,
            compare: allocation.c > allocation.p ? allocation.cf : null,
            discount,
            force,
          });
          return;
        }

        const saleTotal = allocation.p * qty;
        const compareTotal = compareUnit * qty;
        const sale = this.formatTotal(allocation.pf, allocation.p, saleTotal);
        const compare =
          compareTotal > saleTotal
            ? this.formatTotal(allocation.cf || variant.cf, compareUnit, compareTotal)
            : null;
        this.updateCtaPrice({ sale, compare, discount, force });
      }

      // Reuses whatever currency prefix/suffix the shop's own formatted
      // price string already uses (e.g. "$121.00", "121,00 kr") instead of
      // hardcoding "$" — same reasoning as formatTotal()'s string-replace
      // trick above, just for a number that never appears in that string
      // (a savings total, not a scaled copy of it).
      formatMoney(sampleFormatted, amount) {
        const match = sampleFormatted?.match(/^(\D*)[\d.,]+(\D*)$/);
        if (!match) return `$${Math.round(amount)}`;
        const [, prefix, suffix] = match;
        return `${prefix}${Math.round(amount)}${suffix}`;
      }

      // "Save $X vs one-time" (subscribe_bullets_1/2/3 settings) — "$X" is a
      // plain placeholder typed by the merchant, replaced here with the
      // actual amount saved on the selected plan: (one-time per-unit price
      // − subscription per-unit price) × quantity, the same numbers
      // updateCtaPriceForBar() uses for its sale/compare totals.
      //
      // The original "$X" text is cached in data-template the first time
      // it's found — without it, the second render would search the
      // already-replaced text (e.g. "Save $25 vs one-time"), never find
      // "$X" again, and freeze on the first computed amount forever.
      updateSavingsBullet(index) {
        const pricing = this.resolveBarPricing(index);
        if (!pricing) return;
        const { variant, allocation, qty } = pricing;

        const compareUnit = allocation.c || variant.p;
        if (compareUnit <= allocation.p) return;

        const savings = ((compareUnit - allocation.p) * qty) / 100;
        const formatted = this.formatMoney(allocation.cf || variant.cf, savings);

        const list = this.querySelector(`[data-bullets-index="${index}"]`);
        const item = Array.from(list?.querySelectorAll("li") || []).find((li) =>
          (li.dataset.template || li.textContent).includes("$X"),
        );
        if (!item) return;

        item.dataset.template ??= item.textContent;
        item.textContent = item.dataset.template.replace("$X", formatted);
      }

      // Mirrors the native product-form submit button's price display
      // (ProductSubmitButton-... in sections/main-product.liquid): the price
      // the shopper will actually pay, plus the crossed-out price they could
      // have paid instead, right on the Add to Cart button itself.
      // Same "$96.00" → "$96" cleanup as this theme's own native fmt()
      // (sections/main-product.liquid).
      fmtPrice(s) {
        return s ? s.replace(/\.\d+/, "") : "";
      }

      updateCtaPrice({ sale, compare, discount = null, force = false }) {
        const pricesEl = this.querySelector("[data-cta-prices]");
        const saleEl = this.querySelector("[data-cta-sale]");
        const compareEl = this.querySelector("[data-cta-compare]");
        const discountEl = this.querySelector("[data-cta-discount]");
        if (!pricesEl || !saleEl) return;

        if (!sale) {
          pricesEl.style.display = "none";
          return;
        }

        // Kaching writes this exact price span directly and synchronously
        // on its own click handling — same class names as this theme's own
        // native submit button (sections/main-product.liquid), which
        // documents this exact behavior ("kaching fires its own updatePrice
        // synchronously ... if saleEl already has text, kaching already
        // wrote — don't overwrite"). Fighting it by overwriting on our own
        // delayed render is what needed several clicks before the two
        // happened to land on the same value. Only fill in if it hasn't
        // written anything yet (e.g. right after load/switch, before its
        // write lands) — compareEl is always ours, Kaching doesn't handle it.
        // force skips this guard entirely — used by the one-time path,
        // where Kaching never writes this span at all, so there's nothing
        // of its own to avoid fighting; without force, a price left over
        // from an earlier subscribe render (or a different bundle size) would
        // never get replaced.
        const kachingManaged = !!document.querySelector('[name^="kaching-bundles-deal-"]');
        if (force || !kachingManaged || !saleEl.textContent.trim()) {
          saleEl.textContent = this.fmtPrice(sale);
        }

        if (compareEl) {
          if (compare) {
            compareEl.textContent = this.fmtPrice(compare);
            compareEl.style.display = "";
          } else {
            compareEl.style.display = "none";
          }
        }

        if (discountEl) {
          if (discount) {
            discountEl.textContent = `(${discount}% off)`;
            discountEl.style.display = "";
          } else {
            discountEl.style.display = "none";
          }
        }

        pricesEl.style.display = "";
      }

      // Each supply card can have its own "Subscribe & Save" bullet list
      // (set per-plan in the section's settings — see data-bullets-index on
      // the <ul> elements in the liquid). Show only the one matching the
      // currently selected plan, hide the rest.
      renderBulletsFor(index) {
        this.querySelectorAll("[data-bullets-index]").forEach((ul) => {
          ul.hidden = Number(ul.dataset.bulletsIndex) !== index;
        });
      }

      // Same idea as renderBulletsFor(), for the per-plan "Subscribe & Save"
      // badge (data-badge-index in the liquid).
      renderBadgeFor(index) {
        this.querySelectorAll("[data-badge-index]").forEach((badge) => {
          badge.hidden = Number(badge.dataset.badgeIndex) !== index;
        });
      }

      // Same idea again, for the per-plan "Ships every..." shipping text
      // (data-ships-index in the liquid) — entered manually per plan since
      // Kaching Bundles doesn't reliably expose this as readable text.
      renderShipsFor(index) {
        this.querySelectorAll("[data-ships-index]").forEach((el) => {
          el.hidden = Number(el.dataset.shipsIndex) !== index;
        });
      }

      // Manual diagnostics — run `debugProductBanner()` in the console at any
      // time to see bar count, each bar's selected state, and its raw vs.
      // computed price.
      debugKaching() {
        const block = this.nativeRoot()?.querySelector(".kaching-bundles__block");
        const bars = this.nativeBars();

        console.log("[product-banner] ===== Kaching diagnostics =====");

        if (!block) {
          console.log("[product-banner] .kaching-bundles__block not found.");
        } else {
          console.log("[product-banner] block element:", block);
          console.log("[product-banner] block rect:", block.getBoundingClientRect());
        }

        console.log(`[product-banner] ${bars.length} bar(s) found`);
        bars.forEach((bar, i) => {
          console.log(`[product-banner] bar ${i}:`, {
            title: bar.querySelector(".kaching-bundles__bar-title")?.textContent.trim(),
            ...this.readBarData(bar),
          });
        });

        console.log("[product-banner] ===== end diagnostics =====");
      }
    },
  );
}
